import {
  priceDateKey,
  storedDateToLocalDate,
} from '@/utils/circuits/prices/priceDateKey';
import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { Room } from '../findAllRoomCombinations';
import {
  DOUBLE_ROOM_COMBINATIONS,
  INDIVIDUAL_ROOM_COMBINATIONS,
  QUADRUPLE_ROOM_COMBINATIONS,
  TRIPLE_ROOM_COMBINATIONS,
} from '@/constants/HotelRoomTypes';
import {
  getPricingRangeForPeriod,
  PriceWithFormula,
} from '@/utils/circuits/prices/computeBenefitPrices';
import { getCombinationsFromHotelCategory } from './getCombinationsFromHotelCategory';
import {
  getDefaultPricingWindow,
  PricingWindow,
} from '@/utils/circuits/prices/pricingHorizon';
import { addDays, getDay, isAfter, isBefore } from 'date-fns';
import { WEEKDAYS } from '@/constants/weekdays';

type HotelRoomPricingType =
  | 'individualRoomRepartitions'
  | 'doubleRoomRepartitions'
  | 'tripleRoomRepartitions'
  | 'quadrupleRoomRepartitions';

type RoomOptionsType =
  | 'individualRoomOptions'
  | 'doubleRoomOptions'
  | 'tripleRoomOptions'
  | 'quadrupleRoomOptions';

type HotelRoomCategoryType = Required<
  Required<
    CircuitDayFormData['steps'][number]['accommodations']
  >['standardBenefit']
>['hotel']['HotelRoomCategory'][number];

const getCombinationPreferenceScore = (
  combination: Room[],
  totalGuests: number,
): number => {
  const roomSizes = combination
    .map((room) => room[0] + room[1] + room[2])
    .toSorted((a, b) => a - b);

  switch (totalGuests) {
    case 2: {
      // Prefer a single double room over two singles (if both options exist)
      if (roomSizes.length === 1 && roomSizes[0] === 2) return 0;
      return 1;
    }
    case 4: {
      // Prefer a quadruple or two doubles equally (cheapest wins)
      if (roomSizes.length === 1 && roomSizes[0] === 4) return 0;
      if (roomSizes.length === 2 && roomSizes[0] === 2 && roomSizes[1] === 2)
        return 0;
      return 1;
    }
    case 8: {
      // Prefer two quadruples or four doubles equally (cheapest wins)
      if (roomSizes.length === 2 && roomSizes[0] === 4 && roomSizes[1] === 4)
        return 0;
      if (roomSizes.length === 4 && roomSizes.every((size) => size === 2))
        return 0;
      return 1;
    }
    default:
      // No special preference for other totals
      return 1;
  }
};

export const computeHotelPricesForDates = ({
  category,
  nbAdults,
  nbChildren = 0,
  nbAdolescents = 0,
  pricingType = 'byRoom',
  reducedPriceExists = false,
  isReduction = false,
  targetDates,
  pricingWindow,
}: {
  category: HotelRoomCategoryType;
  nbAdults: number;
  nbChildren?: number;
  nbAdolescents?: number;
  pricingType?: 'byPerson' | 'byRoom';
  reducedPriceExists?: boolean;
  isReduction?: boolean;
  targetDates?: Date[];
  pricingWindow?: PricingWindow;
}) => {
  const pricesForDate = new Map<string, PriceWithFormula>();
  const { start: today, end: lastDayOfPricingWindow } =
    pricingWindow ?? getDefaultPricingWindow();

  category.pricings.forEach((tarif) => {
    tarif.periods.forEach((period) => {
      const periodStart = storedDateToLocalDate(period.startDate);
      const periodEnd = storedDateToLocalDate(period.endDate);

      const [rangeStart, rangeEnd] = getPricingRangeForPeriod({
        periodStart,
        periodEnd,
        today,
        lastDayOfPricingWindow,
        targetDates,
      });

      if (isAfter(rangeStart, rangeEnd)) return;

      let currDate = rangeStart;
      const dayAfterEndDate = addDays(rangeEnd, 1);

      while (isBefore(currDate, dayAfterEndDate)) {
        const dayIndex = (getDay(currDate) + 6) % 7;
        const dayName = WEEKDAYS[dayIndex];

        if (period.applicationDays.includes(dayName)) {
          const priceWithFormula = computeHotelRoomPrice({
            category,
            nbAdults,
            pricing: tarif,
            isReduction,
            reducedPriceExists,
            pricingType,
            nbAdolescents,
            nbChildren,
          });

          const prevPrice = pricesForDate.get(priceDateKey(currDate));
          if (
            priceWithFormula != null &&
            (!prevPrice ||
              priceWithFormula.minimumPrice < prevPrice.minimumPrice)
          ) {
            pricesForDate.set(priceDateKey(currDate), priceWithFormula);
          }
        }

        currDate = addDays(currDate, 1);
      }
    });
  });

  return pricesForDate;
};

const computeHotelRoomPrice = ({
  category,
  pricing,
  pricingType = 'byRoom',
  reducedPriceExists = false,
  isReduction = false,
  nbAdults,
  nbChildren = 0,
  nbAdolescents = 0,
}: {
  category: HotelRoomCategoryType;
  pricing: HotelRoomCategoryType['pricings'][number];
  pricingType?: 'byPerson' | 'byRoom';
  reducedPriceExists?: boolean;
  isReduction?: boolean;
  nbAdults: number;
  nbChildren?: number;
  nbAdolescents?: number;
}): PriceWithFormula | null => {
  // Find all room combinations for the given number of attendees
  const allCombinations = getCombinationsFromHotelCategory(
    category,
    nbAdults,
    nbChildren,
    nbAdolescents,
  );

  let minimumPriceInfo: PriceWithFormula | null = null;

  allCombinations.forEach((combination) => {
    const totalCombinationPrice = combination.reduce<PriceWithFormula>(
      (acc, room) => {
        const roomPriceInfo = getHotelRoomPrice(
          category,
          pricing,
          room,
          pricingType,
          reducedPriceExists,
          isReduction,
        );
        return {
          minimumPrice: acc.minimumPrice + roomPriceInfo.minimumPrice,
          formula: acc.formula
            ? `${acc.formula} + ${roomPriceInfo.formula}`
            : roomPriceInfo.formula,
        };
      },
      { minimumPrice: 0, formula: '' },
    );

    const totalGuests = nbAdults + nbChildren + nbAdolescents;
    const preferenceScore = getCombinationPreferenceScore(
      combination,
      totalGuests,
    );

    if (!minimumPriceInfo) {
      minimumPriceInfo = {
        ...totalCombinationPrice,
        // Attach internal preference data for comparison only
        _preferenceScore: preferenceScore,
      } as PriceWithFormula & { _preferenceScore: number };
      return;
    }

    const currentPreferenceScore =
      (minimumPriceInfo as PriceWithFormula & { _preferenceScore: number })
        ._preferenceScore ?? 1;

    if (
      preferenceScore < currentPreferenceScore ||
      (preferenceScore === currentPreferenceScore &&
        totalCombinationPrice.minimumPrice < minimumPriceInfo.minimumPrice)
    ) {
      minimumPriceInfo = {
        ...totalCombinationPrice,
        _preferenceScore: preferenceScore,
      } as PriceWithFormula & { _preferenceScore: number };
    }
  });

  return minimumPriceInfo;
};

export const getHotelRoomPrice = (
  category: HotelRoomCategoryType,
  pricing: HotelRoomCategoryType['pricings'][number],
  room: Room,
  pricingType: 'byPerson' | 'byRoom',
  reducedPriceExists: boolean,
  isReduction: boolean,
): PriceWithFormula => {
  const totalRoom = room[0] + room[1] + room[2];
  const { roomPricingType, roomType, source } =
    getRoomTypeAndPricingFromTotal(totalRoom);

  const indexRoom = source.findIndex(
    (r) => r[0] === room[0] && r[1] === room[1] && r[2] === room[2],
  );
  const indexPricing = category[roomType].indexOf(indexRoom);
  const partition = pricing[roomPricingType][indexPricing];

  if (!partition) return { formula: '', minimumPrice: 0 };

  const { price, additionalPrice, firstSpecialPrice, secondSpecialPrice } =
    partition;
  if (pricingType === 'byRoom')
    return {
      minimumPrice: price,
      formula: `${price}`,
    };

  if (!reducedPriceExists)
    return { minimumPrice: room[0] * price, formula: `${room[0]} × ${price}` };

  const formulaIfNotZero = (
    quantity: number | undefined,
    price: number | undefined,
    isPlus = true,
  ) =>
    quantity && price ? `${isPlus ? '+' : '-'} ${quantity} × ${price}` : '';

  if (!isReduction) {
    return {
      minimumPrice:
        room[0] * price +
        room[1] * (firstSpecialPrice ?? 0) +
        room[2] * (secondSpecialPrice ?? 0),
      formula: `${room[0]} × ${price} ${formulaIfNotZero(room[1], firstSpecialPrice)} ${formulaIfNotZero(room[2], secondSpecialPrice)}`,
    };
  }
  return {
    minimumPrice:
      totalRoom * (price + (additionalPrice ?? 0)) -
      room[1] * (firstSpecialPrice ?? 0) -
      room[2] * (secondSpecialPrice ?? 0),
    formula: `${totalRoom} × ${price + (additionalPrice ?? 0)} ${formulaIfNotZero(room[1], firstSpecialPrice, false)} ${formulaIfNotZero(room[2], secondSpecialPrice, false)}`,
  };
};

export const getHotelCategoryWithOnlyOneRoom = (
  category: HotelRoomCategoryType,
  room: Room,
) => {
  const totalRoom = room[0] + room[1] + room[2];
  const { roomPricingType, roomType, source } =
    getRoomTypeAndPricingFromTotal(totalRoom);

  const indexRoom = source.findIndex(
    (r) => r[0] === room[0] && r[1] === room[1] && r[2] === room[2],
  );
  const indexPricing = category[roomType].indexOf(indexRoom);
  const categoryWithOnlyThisRoom: HotelRoomCategoryType = {
    ...category,
    roomTypes: [
      totalRoom === 1
        ? 'individual'
        : totalRoom === 2
          ? 'double'
          : totalRoom === 3
            ? 'triple'
            : 'quadruple',
    ],
    individualRoomOptions: [],
    doubleRoomOptions: [],
    tripleRoomOptions: [],
    quadrupleRoomOptions: [],
    [roomType]: [indexRoom],
    pricings: category.pricings.map((tarif) => ({
      ...tarif,
      individualRoomRepartitions: [],
      doubleRoomRepartitions: [],
      tripleRoomRepartitions: [],
      quadrupleRoomRepartitions: [],
      [roomPricingType]: [tarif[roomPricingType][indexPricing]],
    })),
  };

  return categoryWithOnlyThisRoom;
};

const getRoomTypeAndPricingFromTotal = (
  total: number,
): {
  roomPricingType: HotelRoomPricingType;
  roomType: RoomOptionsType;
  source: Room[];
} => {
  switch (total) {
    case 1:
      return {
        roomPricingType: 'individualRoomRepartitions',
        roomType: 'individualRoomOptions',
        source: INDIVIDUAL_ROOM_COMBINATIONS as Room[],
      };
    case 2:
      return {
        roomPricingType: 'doubleRoomRepartitions',
        roomType: 'doubleRoomOptions',
        source: DOUBLE_ROOM_COMBINATIONS as Room[],
      };
    case 3:
      return {
        roomPricingType: 'tripleRoomRepartitions',
        roomType: 'tripleRoomOptions',
        source: TRIPLE_ROOM_COMBINATIONS as Room[],
      };
    case 4:
      return {
        roomPricingType: 'quadrupleRoomRepartitions',
        roomType: 'quadrupleRoomOptions',
        source: QUADRUPLE_ROOM_COMBINATIONS as Room[],
      };
    default:
      return {
        roomPricingType: 'individualRoomRepartitions',
        roomType: 'individualRoomOptions',
        source: INDIVIDUAL_ROOM_COMBINATIONS as Room[],
      };
  }
};
